/**
 * The `cypher_write` agent-action lifecycle capability — the first
 * registered permission-gated AI action. Wires the `propose_cypher_writes`
 * tool's SSE events, the Neo4j apply/reject endpoints, and the per-row
 * resume text into the generic registry in `lifecycle-registry.ts`.
 *
 * Importing this module registers the capability as a side effect (the
 * state barrel pulls it in), so routing/resume/panel code only ever talks
 * to the registry, never to cypher specifics.
 */
import { agentWritesApi } from '../../api/agents/agent-writes'
import type { InFlightAgentActionProposal } from '../../slices/agent-orchestrator-slice'
import {
  buildCypherWriteEntriesFromToolInput,
  buildNodeEvidenceEntriesFromToolInput,
  scheduleHydratePendingCypherWrites
} from '../cypher-write-pending'
import {
  isPlanWideRefusalCode,
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionCompleteHydration,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

const CYPHER_WRITE_TOOL_NAME = 'propose_cypher_writes'
// Persists cypher_write-shaped rows (changeKind 'node_evidence') and shares
// the apply/reject endpoints, so it routes here instead of registering a
// parallel capability.
const NODE_EVIDENCE_TOOL_NAME = 'propose_node_evidence'

function readString(record: Record<string, unknown>, key: string): string | null {
  const v = record[key]
  return typeof v === 'string' ? v : null
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
  const v = record[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/**
 * Parse a propose tool's `pending: true` output (async-suspend rail:
 * non-blocking ingest proposals, or a not-yet-upgraded server during rollout).
 * Returns the canonical id hydration, or `null` if the tool didn't return
 * a `pending: true` envelope (validation failure, unexpected error, or
 * stringified output we can't read). Blocking (settled) outputs are
 * handled by `parseSettledToolComplete` before routing ever gets here.
 */
function readCypherWriteProposeOutput(
  parentToolUseId: string,
  output: unknown
): AgentActionCompleteHydration | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const obj = output as Record<string, unknown>
  if (obj.pending !== true) return null
  const planId = readString(obj, 'plan_id')
  if (planId === null) return null
  const rawResults = obj.results
  if (!Array.isArray(rawResults)) return null
  const results: Array<{ toolUseId: string; writeId: string; index?: number }> = []
  for (const raw of rawResults) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const r = raw as Record<string, unknown>
    const toolUseId = readString(r, 'tool_use_id')
    const writeId = readString(r, 'write_id')
    if (toolUseId === null || writeId === null) continue
    results.push({ toolUseId, writeId, index: readNumber(r, 'index') })
  }
  return { parentToolUseId, planId, results }
}

/** Human-readable Neo4j change counts for the resume instruction. */
function applyParts(
  counts:
    | Extract<InFlightAgentActionProposal, { kind: 'cypher_write' }>['appliedCounts']
    | Extract<InFlightAgentActionProposal, { kind: 'graph_write' }>['appliedCounts']
): string {
  if (!counts) return ''
  const parts: string[] = []
  if (counts.nodesCreated) parts.push(`nodesCreated=${counts.nodesCreated}`)
  if (counts.nodesModified) parts.push(`nodesModified=${counts.nodesModified}`)
  if (counts.nodesDeleted) parts.push(`nodesDeleted=${counts.nodesDeleted}`)
  if (counts.relationshipsCreated) parts.push(`relationshipsCreated=${counts.relationshipsCreated}`)
  if (counts.relationshipsDeleted) parts.push(`relationshipsDeleted=${counts.relationshipsDeleted}`)
  if (parts.length === 0) return 'no graph changes'
  return parts.join(', ')
}

export const cypherWriteLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'cypher_write',
  toolName: CYPHER_WRITE_TOOL_NAME,
  aliasToolNames: [NODE_EVIDENCE_TOOL_NAME],
  // Structured envelope proposals share apply/reject/resume with this rail.
  aliasKinds: ['graph_write'],

  parseToolStart(toolUseId, input, startedAtMs) {
    // Dispatch on input shape: `writes[]` is a structured envelope batch,
    // `evidence[]` is a node-evidence batch riding the same rail.
    const graphEntries = buildCypherWriteEntriesFromToolInput(toolUseId, input, startedAtMs)
    if (graphEntries.length > 0) return graphEntries
    return buildNodeEvidenceEntriesFromToolInput(toolUseId, input, startedAtMs)
  },

  parseToolComplete(toolUseId, output) {
    return readCypherWriteProposeOutput(toolUseId, output)
  },

  parseSettledToolComplete(output) {
    return parseSettledProposeOutput(output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingCypherWrites(dispatch, conversationId)
  },

  apply(toolUseId, options) {
    return async (dispatch): Promise<AgentActionApplyOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.applyCypherWrite.initiate({
            toolUseId,
            graphAffecting: true,
            ...(options?.branchId ? { branchId: options.branchId } : {})
          })
        ).unwrap()
        if (result.ok) {
          return { kind: 'success', settledMeta: { appliedCounts: result.counts } }
        }
        // Apply against Neo4j failed (e.g. constraint violation). The
        // server has already moved the row to `state: 'error'`; treat
        // the card as terminal so the plan's resume instruction surfaces
        // the failure to the agent rather than blocking forever.
        return { kind: 'apply_failed', error: result.error ?? 'Apply failed.' }
      } catch (err) {
        // The server answers refused/failed applies with HTTP 409 +
        // `{ ok: false, error }` — a rejected mutation, not a fulfilled
        // one. Settle those as terminal so the refusal (e.g. "main is
        // protected") reaches the card and the plan-resume turn. Branch
        // refusals are plan-wide: every sibling write would fail the same
        // way, so the panel settles the whole plan at once.
        const refused = readStructuredMutationError(err)
        if (refused !== null) {
          return {
            kind: 'apply_failed',
            error: refused.error,
            planWide: isPlanWideRefusalCode(refused.code)
          }
        }
        // Network / unexpected error: leave the row actionable for retry.
        const message = err instanceof Error ? err.message : 'Apply failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  reject(toolUseId, reason) {
    return async (dispatch): Promise<AgentActionRejectOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
        ).unwrap()
        if (result.ok) {
          return { kind: 'success', settledMeta: { rejectionReason: result.reason ?? reason } }
        }
        return { kind: 'reject_failed', error: result.error ?? 'Reject failed.' }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Reject failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  buildResumeRowLine(proposal, displayNumber) {
    if (proposal.kind !== 'cypher_write' && proposal.kind !== 'graph_write') {
      return `${displayNumber}. UNDECIDED — "${proposal.summary}".`
    }
    const summary =
      proposal.kind === 'cypher_write' ? proposal.summary || proposal.cypher : proposal.summary
    const writeRef = proposal.writeId ? ` (write ${proposal.writeId})` : ''
    const terminal = proposal.terminalState
    if (terminal?.kind === 'applied') {
      const counts = applyParts(proposal.appliedCounts)
      return `${displayNumber}. APPLIED — "${summary}"${writeRef}${counts ? `; ${counts}` : ''}.`
    }
    if (terminal?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"${writeRef}; error: ${terminal.error}.`
    }
    if (terminal?.kind === 'rejected') {
      const reason = proposal.rejectionReason || 'rejected by user'
      return `${displayNumber}. REJECTED — "${summary}"${writeRef}; reason: "${reason}".`
    }
    // Defensive — shouldn't happen because the resume turn only fires once
    // every row has a terminal state.
    return `${displayNumber}. UNDECIDED — "${summary}"${writeRef}.`
  }
}

registerAgentActionLifecycle(cypherWriteLifecycleCapability)
